import React, { useState, useEffect } from "react";
import { X, Info, Copy, ClipboardPaste } from "lucide-react";
import frameBg from "../../../Assets/FrameBGDashboard.png";
import { getSeverityTypes } from "../../../Services/MetadataService";
import { getProjectData } from "../../../Services/ProjectService";
import { getProjectTeam } from "../../../Services/ProjectService";
import {
  createTask,
  GetUserBandwidth,
  TaskRestructure,
} from "../../../Services/TaskService";
import NotificationAlert from "../../Alerts/NotificationAlert";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { getCopiedTask, clearCopiedTask } from "../../../utlis/CopyTaskStore";
import {
  flatpickrScheduleDateDisable,
  getNextAllowedScheduleDate,
  isBlockedScheduleDate,
} from "../../../utlis/flatpickrDateRules";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

const CreateTaskModal = ({ show, onClose, onCreated }) => {
  const navigate = useNavigate();
  const [newTask, setNewTask] = useState({
    Description: "",
    Severity: "",
    ProjectUID: "",
    AssigneToUID: "",
    AssignedByUID: "",
    EstimatedComplete: "",
  });

  const [severityTypes, setSeverityTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formKey, setFormKey] = useState(Date.now());
  const [isAssignByAllowed, setIsAssignByAllowed] = useState(false);
  const loggedInUserName = Cookies.get("Orbit_UserName") || "";
  //Get User Bandwidth States
  const [bandwidthLoading, setBandwidthLoading] = useState(false);
  const [bandwidthList, setBandwidthList] = useState([]);
  const [bandwidthError, setBandwidthError] = useState("");

  const isFutureDateTime = (date) => {
    if (!date) return false;
    return date.getTime() > new Date().getTime();
  };

  // Load dropdown metadata
  useEffect(() => {
    if (show) {
      loadSeverity();
      loadProjects();
    }
  }, [show]);

  // Reset on modal open
  useEffect(() => {
    if (show) {
      setFormKey(Date.now()); //  Forces rerender & resets fields

      const copied = getCopiedTask();

      if (copied) {
        setNewTask({
          Description: copied.description || "",
          Severity: copied.severity || "",
          ProjectUID: copied.projectUID || "",
          AssigneToUID: copied.assignedToUID || "",
          AssignedByUID: copied.assignedByUID || "",
          EstimatedComplete: copied.estimatedComplete || "",
        });

        if (copied.projectUID) {
          loadTeam(copied.projectUID);
        }

        if (copied.assignedToUID) {
          loadUserBandwidth(copied.assignedToUID);
        }
      } else {
        setNewTask({
          Description: "",
          Severity: "",
          ProjectUID: "",
          AssigneToUID: "",
          AssignedByUID: "",
          EstimatedComplete: "",
        });
      }

      setAttachments([]);
      setErrors({});
      setAlert(null);
      setLoading(false);

      setBandwidthList([]);
      setBandwidthError("");
      setBandwidthLoading(false);
    }
  }, [show]);

  const loadSeverity = async () => {
    try {
      const res = await getSeverityTypes();
      setSeverityTypes(res.data?.data || []);
    } catch (err) {
      if (err.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: err.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      console.error("Error loading severity types", err);
    }
  };

  const loadProjects = async () => {
    try {
      const isAdmin = window.location.pathname.includes("/Admin");
      const res = await getProjectData(isAdmin);
      setProjects(res.data?.data || []);
    } catch (err) {
      if (err.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: err.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      console.error("Error loading projects", err);
    }
  };

  // Load team when project changes
  useEffect(() => {
    if (newTask.ProjectUID) loadTeam(newTask.ProjectUID);
  }, [newTask.ProjectUID]);

  const loadTeam = async (projectUID) => {
    try {
      const res = await getProjectTeam(projectUID);
      setTeam(res.data?.teamMemebers || []);
    } catch (err) {
      if (err.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: err.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      console.error("Error loading project team", err);
    }
  };

  // Input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTask((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "AssigneToUID") {
      if (!value) {
        setBandwidthList([]);
        setBandwidthError("");
        setBandwidthLoading(false);
        setIsAssignByAllowed(false);
        return;
      }
      const selectedMember = team.find((m) => m.userUID === value);
      const selectedName = selectedMember?.name || "";

      loadUserBandwidth(value);

      if (selectedName === loggedInUserName) {
        // Same → enable AssignBy
        setIsAssignByAllowed(true);
      } else {
        // Different → disable AssignBy
        setIsAssignByAllowed(false);

        // Clear AssignedByUID when disabled
        setNewTask((prev) => ({ ...prev, AssignedByUID: "" }));
      }
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!newTask.Description.trim())
      newErrors.Description = "Description is required";
    if (!newTask.Severity) newErrors.Severity = "Severity is required";
    if (!newTask.ProjectUID) newErrors.ProjectUID = "Project is required";
    if (!newTask.AssigneToUID)
      newErrors.AssigneToUID = "Assigne to is required";
    if (!newTask.EstimatedComplete) {
      newErrors.EstimatedComplete = "Estimated date required";
    } else {
      const selectedDate = new Date(newTask.EstimatedComplete);
      if (!isFutureDateTime(selectedDate)) {
        newErrors.EstimatedComplete =
          "Estimated completion must be a future date & time";
      }
    }

    return newErrors;
  };

  // File upload logic
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    const totalFiles = attachments.length + files.length;
    if (totalFiles > 5) {
      setAlert({
        type: "error",
        message: "You can upload a maximum of 5 attachments.",
      });
      e.target.value = "";
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const sizeLimits = {
      "image/jpeg": 200 * 1024,
      "image/png": 200 * 1024,
      "application/pdf": 5 * 1024 * 1024,
      "application/msword": 2 * 1024 * 1024,
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        2 * 1024 * 1024,
      "application/vnd.ms-excel": 2 * 1024 * 1024,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        2 * 1024 * 1024,
    };

    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        setAlert({
          type: "error",
          message:
            "Invalid attachment. Allowed: JPEG, PNG ≤ 200KB, Word/Excel ≤ 2MB, PDF ≤ 5MB",
        });
        e.target.value = "";
        return;
      }
      if (file.size > sizeLimits[file.type]) {
        setAlert({
          type: "error",
          message:
            "Invalid attachment size. Allowed: JPEG/PNG ≤ 200KB, Word/Excel ≤ 2MB, PDF ≤ 5MB",
        });
        e.target.value = "";
        return;
      }
    }

    setAttachments((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Submit
  const handleCreateTask = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setCreateLoading(true);

      const formData = new FormData();
      formData.append("description", newTask.Description);
      formData.append("severity", newTask.Severity);
      formData.append("projectUID", newTask.ProjectUID);
      formData.append("assigneToUID", newTask.AssigneToUID);
      formData.append("assignedByUID", newTask.AssignedByUID);
      formData.append("estimatedComplete", newTask.EstimatedComplete);

      attachments.forEach((file) => formData.append("Attachment", file));

      const response = await createTask(formData);

      const backendMessage =
        response?.data?.message ||
        response?.data?.Message ||
        "Task created successfully";

      if (response.data?.status === "Success") {
        setAlert({ type: "success", message: backendMessage });
        clearCopiedTask();
        setTimeout(() => {
          onCreated();
          onClose();
        }, 3500);
      } else {
        setAlert({ type: "error", message: backendMessage });
      }
    } catch (error) {
      if (error.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: error.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.Message ||
        "Server error";

      setAlert({ type: "error", message: backendMessage });
    } finally {
      setCreateLoading(false);
    }
  };

  //Get User Bandwidth
  const COLORS = {
    assigned: "#05A9E8",
    inProgress: "#005B9E",
    reOpen: "#E3E7F7",
  };

  const loadUserBandwidth = async (userUID) => {
    if (!userUID) return;

    try {
      setBandwidthLoading(true);
      setBandwidthError("");
      setBandwidthList([]);

      const res = await GetUserBandwidth(userUID);

      if (res.data?.status === "Success") {
        setBandwidthList(res.data.details || []);
      } else {
        setBandwidthError("Unable to fetch user bandwidth");
      }
    } catch (err) {
      if (err.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: err.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      setBandwidthError("Server error while fetching bandwidth");
    } finally {
      setBandwidthLoading(false);
    }
  };

  const groupBandwidthByDate = (data) => {
    const grouped = {};

    data.forEach((item) => {
      const dateKey = new Date(item.expectedCompletion).toLocaleDateString(
        "en-GB",
      );

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          assigned: 0,
          inProgress: 0,
          reOpen: 0,
          total: 0,
        };
      }

      grouped[dateKey].total += 1;

      if (item.statusName === "Assigned") grouped[dateKey].assigned += 1;
      if (item.statusName === "In Progress") grouped[dateKey].inProgress += 1;
      if (item.statusName === "Re-open") grouped[dateKey].reOpen += 1;
    });

    return Object.values(grouped);
  };

  const TaskCircle = ({ count }) => {
    return (
      <div className="relative w-11 h-11">
        <svg className="w-full h-full rotate-[-90deg]" viewBox="0 0 36 36">
          {/* background ring */}
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            stroke="#E6EAF5"
            strokeWidth="3"
          />

          {/* blue arc */}
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            stroke={COLORS.inProgress}
            strokeWidth="3"
            strokeDasharray="100"
            strokeDashoffset="25"
            strokeLinecap="round"
          />
        </svg>

        <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-gray-800">
          {count}
        </div>
      </div>
    );
  };

  const BandwidthBar = ({ assigned, inProgress, reOpen, total }) => {
    const a = (assigned / total) * 100;
    const i = (inProgress / total) * 100;
    const r = (reOpen / total) * 100;

    return (
      <div className="w-full">
        {/* bar */}
        <div className="flex h-9 rounded-md overflow-hidden bg-gray-100">
          {assigned > 0 && (
            <div
              style={{ width: `${a}%`, backgroundColor: COLORS.assigned }}
              className="flex items-center justify-center text-white text-sm font-medium"
            >
              {assigned}/{total}
            </div>
          )}

          {inProgress > 0 && (
            <div
              style={{ width: `${i}%`, backgroundColor: COLORS.inProgress }}
              className="flex items-center justify-center text-white text-sm font-medium"
            >
              {inProgress}/{total}
            </div>
          )}

          {reOpen > 0 && (
            <div
              style={{ width: `${r}%`, backgroundColor: COLORS.reOpen }}
              className="flex items-center justify-center text-gray-800 text-sm font-medium"
            >
              {reOpen}/{total}
            </div>
          )}
        </div>
      </div>
    );
  };

  const BandwidthLegend = () => (
    <div className="flex gap-6 text-xs text-gray-700 mb-3">
      <div className="flex items-center gap-2">
        <span
          className="w-3 h-3 rounded-sm"
          style={{ backgroundColor: COLORS.assigned }}
        />
        <span>Assigned Task</span>
      </div>

      <div className="flex items-center gap-2">
        <span
          className="w-3 h-3 rounded-sm"
          style={{ backgroundColor: COLORS.inProgress }}
        />
        <span>In Progress Task</span>
      </div>

      <div className="flex items-center gap-2">
        <span
          className="w-3 h-3 rounded-sm border"
          style={{ backgroundColor: COLORS.reOpen }}
        />
        <span>Re-Open</span>
      </div>
    </div>
  );

  const handleGenerateTask = async () => {
    try {
      setAiLoading(true);

      const payload = {
        taskDescription: newTask.Description,
      };

      const res = await TaskRestructure(payload);

      if (res.data?.status === "Success") {
        const aiText = res.data.taskDescription;
        setNewTask((prev) => ({
          ...prev,
          Description: aiText,
        }));
      }
    } catch (err) {
      console.error(err);
      setAlert({
        type: "error",
        message: "Something went wrong while generating task",
      });
    } finally {
      setAiLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-end z-50 transition-opacity duration-300 opacity-100"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white w-full max-w-4xl h-full shadow-2xl border-l animate-slideFadeIn flex flex-col overflow-hidden rounded-l-2xl"
      >
        {/* HEADER */}
        <div
          className="flex justify-between items-center px-8 py-6 border-b p-5 text-white shadow-md bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${frameBg})` }}
        >
          <h2 className="text-2xl font-semibold">Create New Task</h2>
        </div>

        {/* BODY */}
        <div
          key={formKey}
          className="flex-1 overflow-y-auto px-8 py-6 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Description */}
            <div className="col-span-2">
              <div className="flex justify-between items-center mt-2">
                <label className="text-sm font-medium text-gray-700">
                  Task Description <span className="text-red-500">*</span>
                </label>

                <button
                  type="button"
                  onClick={handleGenerateTask}
                  disabled={aiLoading || !newTask.Description}
                  className="px-6 py-2 text-xs bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {aiLoading ? "Generating..." : "Improve with AI"}
                </button>
              </div>

              <div className="relative">
                {aiLoading ? (
                  <div className="w-full h-[150px] rounded-md bg-gray-200 animate-pulse flex flex-col gap-2 p-3">
                    <div className="h-3 bg-gray-300 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-300 rounded w-full"></div>
                    <div className="h-3 bg-gray-300 rounded w-5/6"></div>
                    <div className="h-3 bg-gray-300 rounded w-2/3"></div>
                  </div>
                ) : (
                  <textarea
                    name="Description"
                    value={newTask.Description}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        handleInputChange(e);
                      }
                    }}
                    rows={6}
                    maxLength={500}
                    className="w-full p-2 border rounded-md mt-1 text-sm"
                    placeholder="Enter task description (max 500 characters)"
                  />
                )}
              </div>
              <div className="flex justify-end text-xs text-gray-500 mt-1">
                {newTask.Description.length} / 500
              </div>

              {errors.Description && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.Description}
                </p>
              )}
            </div>

            {/* Severity */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Severity <span className="text-red-500">*</span>
              </label>
              <select
                name="Severity"
                value={newTask.Severity}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md mt-1 text-sm"
              >
                <option value="">Select severity</option>
                {severityTypes.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {errors.Severity && (
                <p className="text-xs text-red-500 mt-1">{errors.Severity}</p>
              )}
            </div>

            {/* Project */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Project <span className="text-red-500">*</span>
              </label>
              <select
                name="ProjectUID"
                value={newTask.ProjectUID}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md mt-1 text-sm"
              >
                <option value="">Select project</option>
                {projects.map((proj) => (
                  <option key={proj.projectUID} value={proj.projectUID}>
                    {proj.projectName}
                  </option>
                ))}
              </select>
              {errors.ProjectUID && (
                <p className="text-xs text-red-500 mt-1">{errors.ProjectUID}</p>
              )}
            </div>

            {/* Assign To */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Assign To <span className="text-red-500">*</span>
              </label>

              <select
                name="AssigneToUID"
                value={newTask.AssigneToUID}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md mt-1 text-sm"
              >
                <option value="">Select team member</option>
                {team.map((member) => (
                  <option key={member.userUID} value={member.userUID}>
                    {member.name} ({member.role})
                  </option>
                ))}
              </select>

              {errors.AssigneToUID && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.AssigneToUID}
                </p>
              )}
            </div>

            {/* Assign By */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Assign By
              </label>

              <select
                name="AssignedByUID"
                value={newTask.AssignedByUID}
                onChange={handleInputChange}
                disabled={!isAssignByAllowed}
                className={`w-full p-2 border rounded-md mt-1 text-sm ${
                  !isAssignByAllowed ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
              >
                <option value="">Select team member</option>
                {team.map((member) => (
                  <option key={member.userUID} value={member.userUID}>
                    {member.name} ({member.role})
                  </option>
                ))}
              </select>

              {errors.AssignedByUID && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.AssignedByUID}
                </p>
              )}
            </div>

            {/* Estimated Completion */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Estimated Completion <span className="text-red-500">*</span>
              </label>

              <Flatpickr
                value={
                  newTask.EstimatedComplete
                    ? new Date(newTask.EstimatedComplete)
                    : null
                }
                onReady={(selectedDates, dateStr, instance) => {
                  // force initial valid range
                  const dt = getNextAllowedScheduleDate(
                    instance.selectedDates[0] || new Date(),
                  );

                  const min = new Date(dt);
                  min.setHours(9, 30, 0, 0);

                  const max = new Date(dt);
                  max.setHours(18, 0, 0, 0);

                  instance.config.minTime = "09:30";
                  instance.config.maxTime = "18:00";
                }}
                onValueUpdate={(selectedDates, dateStr, instance) => {
                  if (!selectedDates.length) return;

                  const dt = selectedDates[0];

                  if (isBlockedScheduleDate(dt)) {
                    const nextAllowedDate = getNextAllowedScheduleDate(
                      new Date(Math.max(dt.getTime(), Date.now())),
                    );
                    nextAllowedDate.setHours(9, 30, 0, 0);
                    instance.setDate(nextAllowedDate, false);
                    return;
                  }

                  const min = new Date(dt);
                  min.setHours(9, 30, 0, 0);

                  const max = new Date(dt);
                  max.setHours(18, 0, 0, 0);

                  // if time goes below 9:30 AM → pull it back
                  if (dt < min) {
                    instance.setDate(min, false);
                  }

                  // if above 6 PM → push to next-day 9:30 AM
                  if (dt > max) {
                    const nextDay = getNextAllowedScheduleDate(
                      new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() + 1),
                    );
                    nextDay.setHours(9, 30, 0, 0);
                    instance.setDate(nextDay, false);
                  }
                }}
                onClose={(selectedDates, dateStr, instance) => {
                  if (!selectedDates?.length) return;

                  const final = instance.selectedDates[0];
                  if (isBlockedScheduleDate(final)) {
                    setErrors((prev) => ({
                      ...prev,
                      EstimatedComplete:
                        "Sundays and previous dates are not allowed",
                    }));

                    setAlert({
                      type: "error",
                      message:
                        "Sundays and previous dates are not allowed for estimated completion.",
                    });

                    instance.clear();

                    setNewTask((prev) => ({
                      ...prev,
                      EstimatedComplete: "",
                    }));

                    return;
                  }

                  if (!isFutureDateTime(final)) {
                    setErrors((prev) => ({
                      ...prev,
                      EstimatedComplete: "Date & time must be in the future",
                    }));

                    setAlert({
                      type: "error",
                      message:
                        "Estimated completion date & time must be in the future.",
                    });

                    instance.clear();

                    setNewTask((prev) => ({
                      ...prev,
                      EstimatedComplete: "",
                    }));

                    return;
                  }
                  const localISO = new Date(
                    final.getTime() - final.getTimezoneOffset() * 60000,
                  )
                    .toISOString()
                    .slice(0, 19);

                  handleInputChange({
                    target: {
                      name: "EstimatedComplete",
                      value: localISO,
                    },
                  });
                }}
                options={{
                  enableTime: true,
                  dateFormat: "d-m-Y h:i K", // show AM/PM
                  time_24hr: false,
                  minuteIncrement: 15,
                  allowInput: true,
                  minDate: "today",
                  disable: flatpickrScheduleDateDisable,

                  minTime: "09:30",
                  maxTime: "18:00",
                }}
                className="w-full p-2 border rounded-md mt-1 text-sm"
                placeholder="dd-mm-yyyy --:--"
              />

              {errors.EstimatedComplete && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.EstimatedComplete}
                </p>
              )}
            </div>

            {/* Attachment */}
            <div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Attachments
                </label>

                <div className="relative group">
                  <Info
                    size={16}
                    className="text-gray-500 cursor-pointer hover:text-gray-700"
                  />
                </div>
              </div>

              <input
                type="file"
                name="Attachment"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                multiple
                className="w-full p-2 border rounded-md mt-1 text-sm"
              />
            </div>

            {/* User Bandwidth Section */}
            <div className="col-span-2 mt-4">
              {bandwidthLoading && (
                <p className="text-xs text-blue-600">
                  Loading user bandwidth...
                </p>
              )}
              {bandwidthError && (
                <p className="text-xs text-red-500">{bandwidthError}</p>
              )}

              {newTask.AssigneToUID &&
                !bandwidthLoading &&
                bandwidthList.length > 0 && (
                  <>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                      Current Assigned Tasks ({bandwidthList.length})
                    </h3>

                    <div className="w-full border rounded-lg p-4">
                      {/* User Bandwidth Section */}
                      <div className="col-span-2 mt-4">
                        {bandwidthLoading && (
                          <p className="text-xs text-blue-600">
                            Loading user bandwidth...
                          </p>
                        )}

                        {!bandwidthLoading && bandwidthList.length > 0 && (
                          <>
                            {/* <h3 className="text-sm font-semibold text-gray-700 mb-3">
                              Current Assigned Tasks
                            </h3> */}

                            <div className="bg-white border rounded-lg divide-y">
                              <div className="bg-white border rounded-lg p-4">
                                {/* Legend – shown ONCE */}
                                <BandwidthLegend />

                                <div className="divide-y">
                                  {groupBandwidthByDate(bandwidthList).map(
                                    (row, idx) => (
                                      <div
                                        key={idx}
                                        className="flex items-center gap-4 py-4"
                                      >
                                        {/* Circle */}
                                        <TaskCircle count={row.total} />

                                        {/* Date */}
                                        <div className="w-28 text-sm text-gray-600">
                                          {row.date}
                                        </div>

                                        {/* Bar */}
                                        <BandwidthBar
                                          assigned={row.assigned}
                                          inProgress={row.inProgress}
                                          reOpen={row.reOpen}
                                          total={row.total}
                                        />
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
            </div>

            {/* Attachment Preview */}
            {attachments.length > 0 && (
              <div className="col-span-2 mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Attachments ({attachments.length})
                </h3>

                <div className="w-full bg-gray-50 border rounded-lg p-4 max-h-48 overflow-y-auto">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm border mb-2"
                    >
                      <div className="flex items-center gap-3">
                        {file.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            className="w-12 h-12 rounded object-cover border"
                            alt="preview"
                          />
                        ) : (
                          <div className="w-12 h-12 flex items-center justify-center bg-gray-200 rounded text-gray-600 text-sm">
                            {file.name.split(".").pop().toUpperCase()}
                          </div>
                        )}

                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={() => removeAttachment(index)}
                        className="text-red-500 hover:text-red-700 text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-8 py-4 border-t bg-white flex justify-end gap-3">
          <button
            onClick={() => {
              setBandwidthList([]);
              setBandwidthError("");
              setBandwidthLoading(false);
              onClose();
            }}
            className="px-5 py-2 text-sm border rounded-md text-gray-600 hover:bg-gray-100"
            disabled={loading}
          >
            Cancel
          </button>

          <button
            onClick={handleCreateTask}
            className="px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow hover:opacity-90"
            disabled={createLoading}
          >
            {createLoading ? "Creating..." : "Create Task"}
          </button>
        </div>
      </div>

      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default CreateTaskModal;
